import type {
  Series,
  YAxisOptions,
  XAxisOptions,
  AxisLabelsFormatterContextObject,
  TooltipOptions,
  LegendOptions,
  Point,
} from 'highcharts'
import type { ReactNode } from 'react'
import type {
  ExtendedHighchartsOptions,
  ExtendedSerie,
  GraphConfig,
  IBuilderConfig,
  IChartConfigApiModel,
  ISerie,
} from '~/types/graph-builder'

import { getNumberWithSpacesAndDot } from './number'

import { SECONDS_IN_DAY } from '~/components/Chart/options'
import { analyticsColors } from '~/const/chart'
import { MAX_MAIN_SERIES_TO_SHOW, MAX_SERIES_TO_SHOW } from '~/const/graphs-builder'
import dayjs from '~/utils/dayjs'

const computeSerieName = (serie: ISerie, withBase: boolean): string => {
  let name = ''

  if (withBase) {
    name = computeBaseSerieName(serie)
  }

  if (serie.breakdowns) {
    Object.entries(serie.breakdowns).forEach(([, value]) => {
      if (name) {
        name += ' - '
      }
      name += value
    })
  }

  if (serie.name.endsWith('_trend')) {
    if (name) {
      name += ' - '
    }
    name += 'Trend'
    if (serie.name.includes('_inf_')) {
      name += ' (lower bound)'
    } else if (serie.name.includes('_sup_')) {
      name += ' (upper bound)'
    }
  }

  return name
}

const computeBaseSerieName = (serie: ISerie): string => {
  let name = serie.name.replaceAll('_inf', '')

  name = name.replaceAll('_sup', '')
  name = name.replaceAll('_trend', '')

  if (serie.breakdowns) {
    Object.entries(serie.breakdowns).forEach(([, value]) => {
      name = name.replaceAll(value, '')
    })
  }

  name = name.replaceAll('_', ' ')
  name = name.replaceAll('  ', ' ')

  // remove extra spaces
  name = name.replace(/\s+/g, ' ').trim()

  // uppercase every first letter of each word
  return name.replace(/\b\w/g, (l) => l.toUpperCase())
}

const normalizedName = (name: string, series: Series[]): string => {
  // NOTE: exclude event series from the computation
  // @ts-expect-error initialType does not exist on type Series
  const seriesNames = series.filter(({ initialType }) => initialType !== 'flags').map(({ name }) => name)
  const tokens = name.split(' ')
  const remainingTokens = []

  for (let i = 0; i < tokens.length; i++) {
    if (i + 1 >= tokens.length) remainingTokens.push(tokens[i])
    else {
      const substr = `${tokens[i]} ${tokens[i + 1]}`
      if (!seriesNames.every((name) => name.includes(substr))) {
        remainingTokens.push(tokens[i])
      }
    }
  }

  return remainingTokens.join(' ')
}

export const populateSeries = (config: GraphConfig): ExtendedSerie[] => {
  const tmpsSeriesArray: ExtendedSerie[] = []

  const distinctBaseSeriesName = new Set()
  let nbSeries = config.series.length
  let addPrefixToSeriesName = true

  if (nbSeries > 1) {
    config.series.forEach((singleSeries) => {
      distinctBaseSeriesName.add(computeBaseSerieName(singleSeries))
    })

    nbSeries = distinctBaseSeriesName.size
    addPrefixToSeriesName = nbSeries > 1
  }

  config.series.forEach((singleSeries) => {
    const yAxisIndex = config.yAxes.findIndex(({ name }) => singleSeries.yAxis === name)

    // @ts-expect-error Type 'undefined' is not assignable to type '"zigzag"'.ts(2322)
    const tmpSeries: ExtendedSerie = {
      data:
        config.xAxis.dataType === 'dai'
          ? singleSeries.data.map((d) => ({ name: `${d[0]}`, y: d[1] }))
          : [...singleSeries.data],
      id: singleSeries.name,
      marker: {
        symbol: 'circle',
      },
      name: computeSerieName(singleSeries, addPrefixToSeriesName),
      type: singleSeries.graphType?.includes('column') ? 'column' : singleSeries.graphType,
      yAxis: yAxisIndex !== -1 ? yAxisIndex : 0,
    }

    if (singleSeries.name.endsWith('_trend')) {
      let parentID = singleSeries.name.replace('_trend', '')

      if (singleSeries.name.includes('_inf') || singleSeries.name.includes('_sup')) {
        tmpSeries.dashStyle = 'Dot'
        // Recraft the parentID to allow highcharts to show/hide all related series when clicking on the legend
        parentID = parentID
          .replace('_inf_by_', '_by_')
          .replace('_sup_by_', '_by_')
          .replace('_inf', '')
          .replace('_sup', '')
      } else {
        tmpSeries.opacity = 0.4
      }

      tmpSeries.marker.enabled = false
      tmpSeries.linkedTo = parentID
    }

    tmpsSeriesArray.push(tmpSeries)
  })

  tmpsSeriesArray.sort((a, b) => {
    if (!a.name) return 1
    if (!b.name) return -1
    return b.name.localeCompare(a.name, undefined, { numeric: true, sensitivity: 'base' })
  })

  const colorIndexBySerieID: { [key: string]: number } = {}
  let currentIndex = 0

  tmpsSeriesArray.forEach((singleSeries) => {
    if (!singleSeries.linkedTo && singleSeries.id) {
      colorIndexBySerieID[singleSeries.id] = currentIndex
      singleSeries.colorIndex = currentIndex
      currentIndex++
    }
  })

  tmpsSeriesArray.forEach((singleSeries) => {
    if (singleSeries.linkedTo && colorIndexBySerieID[singleSeries.linkedTo] !== undefined) {
      singleSeries.colorIndex = colorIndexBySerieID[singleSeries.linkedTo]
    }
  })

  return tmpsSeriesArray
}

export const handleAxisOverrides = (
  options: ExtendedHighchartsOptions,
  config: GraphConfig
): [YAxisOptions[], XAxisOptions] => {
  const yAxis: YAxisOptions[] = [
    {
      labels: {
        formatter: function (this: AxisLabelsFormatterContextObject) {
          if (typeof this.value !== 'number') {
            return this.value
          }

          if (options.plotOptions?.column?.stacking === 'percent') return this.value.toFixed(0) + '%'

          return formatValue(config.yAxes?.[0], this.value)
        },
        style: {
          fontSize: '1.2rem',
        },
      },
      opposite: false,
      title: {
        text: '',
      },
      type: 'linear',
    },
  ]

  if (config.yAxes.length > 1) {
    yAxis.push({
      labels: {
        formatter: function (this: AxisLabelsFormatterContextObject) {
          if (typeof this.value !== 'number') {
            return this.value
          }

          if (options.plotOptions?.column?.stacking === 'percent') return this.value.toFixed(0) + '%'

          return formatValue(config.yAxes[1], this.value)
        },
        style: {
          fontSize: '1.2rem',
        },
      },
      opposite: true,
      title: {
        text: '',
      },
      type: 'linear',
    })
  }

  const xAxis: XAxisOptions = {
    gridLineWidth: 0,
    labels: {
      formatter: function (this: AxisLabelsFormatterContextObject) {
        return formatPointValue(config, this.value)
      },
      style: {
        fontSize: '1.2rem',
      },
    },
    ...(config.xAxis.dataType !== 'dai'
      ? {
          minRange: SECONDS_IN_DAY,
          tickInterval: SECONDS_IN_DAY,
          type: 'datetime',
        }
      : {
          minRange: undefined,
          tickInterval: 1,
          tickmarkPlacement: 'on',
          type: 'category',
        }),
  }

  return [yAxis, xAxis]
}

export const formatValue = ({ precision, unit }: { precision: number; unit: string }, value: number): string => {
  if (precision < 0) {
    precision = 2
  }

  switch (unit) {
    case 'duration_in_seconds':
      const date = new Date(0)

      date.setSeconds(value)

      const isoDate = date.toISOString()

      return `${isoDate.substring(11, Math.min(11 + 8 + precision, isoDate.length))}`
    case 'euro':
      return `${getNumberWithSpacesAndDot(value, precision)}€`
    case 'percent':
      return `${value ? (value * 100).toFixed(precision) : 0}%`
    default:
      return `${getNumberWithSpacesAndDot(value, precision)}`
  }
}

const formatPointValue = (config: GraphConfig, value: any): string | any => {
  switch (config.xAxis.dataType) {
    case 'datetime':
      return dayjs(value).utc().format('MMM DD')
    case 'dai':
      return `D${value}`
    default:
      return value
  }
}

export const handleTooltip = (options: ExtendedHighchartsOptions, config: GraphConfig): TooltipOptions => {
  const tooltip: TooltipOptions = {
    backgroundColor: '#FFFFFF',
    borderColor: '#1d1d1d',
    followPointer: true,
    footerFormat: '</div>',
    formatter: function (this, tooltip) {
      // NOTE: As the global tooltip formatter prevails on the pointFormatter, we need to detect the event series and skip
      // the formatter for its tooltip:
      if (this.series.userOptions.type === 'flags') return tooltip.defaultFormatter.call(this, tooltip)

      const yAxisIndex = Number(this.series.userOptions.yAxis) || 0
      const { precision, unit } = config.yAxes[yAxisIndex]

      const total = this.total || 0

      const renderStackingTotal = () => {
        return this.y && this.y > 0 && this.y !== this.total
          ? `<br /><b>TOTAL:</b> ${formatValue({ precision, unit }, total)}`
          : ''
      }

      // NOTE: this.points exist when shared property is true
      // @ts-expect-error this.points does not exist on type Point
      const htmlPointsList = this.points
        ?.map((point: Point) => {
          const yAxisName = config.series.find(
            ({ name }) => computeSerieName({ data: [], name }, true) === point.series.name
          )?.yAxis
          const yAxis = config.yAxes.find(({ name }) => name === yAxisName)
          const pointUnit = yAxis?.unit || unit
          const pointPrecision = yAxis?.precision ?? precision

          let mainData: string
          let additionalData = ''
          if (total !== 0) {
            // Add the percentage for stacked column charts
            mainData = (((point.y ?? 0) / total) * 100).toFixed(2) + '%'
            additionalData = ` (${formatValue({ precision: pointPrecision, unit: pointUnit }, point.y ? point.y : 0)})`
          } else {
            mainData = formatValue({ precision: pointPrecision, unit: pointUnit }, point.y ? point.y : 0)
          }

          const name =
            point.series.name == '' ? 'Raw data' : normalizedName(point.series.name, point.series.chart.series)

          const borderStyle = name.includes('upper bound') || name.includes('lower bound') ? 'dashed' : 'solid'
          const borderOpacity = name.endsWith('Trend') ? 0.4 : 1

          return `
                  <tr style="position: relative;">
                    <td >
                      <div style="border-width: 1px; border-color: ${point.color}; border-style: ${borderStyle}; border-radius: 25px; position: absolute; top: 7.5px; width: 15px; opacity: ${borderOpacity}"></div>
                      <div style="padding-left: 20px;">${name}</div>
                    </td>
                    <td style="padding-left: 5px;">
                      ${mainData}
                      ${additionalData}
                    </td>
                  </tr>
                `
        })
        .join('')

      return `
          <div>
            <div>
              <div>
                  ${
                    config.xAxis.dataType === 'dai'
                      ? formatPointValue(config, this.name)
                      : formatPointValue(config, this.category)
                  }
              </div>
              <table>
                  ${htmlPointsList}
              </table>
              ${options.plotOptions?.column?.stacking !== undefined ? renderStackingTotal() : ''}
            </div>
          </div>`
    },
    outside: true,
    padding: 12,
    positioner: function (labelWidth, labelHeight, point) {
      if (this.chart.fullscreen.isOpen) {
        const chart = this.chart

        //Default position
        let tooltipX = point.plotX + chart.plotLeft - labelWidth / 2
        let tooltipY = point.plotY + chart.plotTop + 10

        // Bottom side adjustement
        if (tooltipY + labelHeight > chart.chartHeight) {
          tooltipY = point.plotY + chart.plotTop - labelHeight - 10
        }

        // Left side adjustement
        if (tooltipX < 0) {
          tooltipX = 0
        }

        // Right side adjustement
        if (tooltipX + labelWidth > chart.chartWidth) {
          tooltipX = chart.chartWidth - labelWidth
        }

        return {
          x: tooltipX,
          y: tooltipY,
        }
      } else {
        return {
          // keep the tooltip in graph container in x-axis, touch it carefully
          x: Math.min(
            Math.max(point.plotX + this.chart.plotLeft - labelWidth / 2, 0),
            this.chart.chartWidth - labelWidth
          ),
          y: point.plotY + this.chart.plotTop + 10,
        }
      }
    },
    shape: 'rect',
    shared: true,
    split: false,
    useHTML: true,
  }

  return tooltip
}

export const filterSeriesIfNeeded = (options: ExtendedHighchartsOptions): ExtendedSerie[] => {
  // If the number of series is acceptable, return the config as is
  // (Don't include children series in the filter decision like trends)
  if (options.series.length <= MAX_SERIES_TO_SHOW) {
    return options.series.map((s) => ({ ...s, visible: true }))
  }

  // compute the total of all series
  // and the total of each series
  const totalBySeries: { name: string; total: number }[] = []

  options.series.forEach((singleSeries) => {
    // Ignore children series / trends and empty series
    if (singleSeries.linkedTo || !singleSeries.data) {
      return
    }

    const totalForCurrent = singleSeries.data.reduce(
      (totalComputedForThis, point) => totalComputedForThis + (point.length > 1 ? point[1] : point[0]),
      0
    )

    totalBySeries.push({ name: singleSeries.name as string, total: totalForCurrent })
  }, 0)

  // sort the series by total and compute the number of series to keep
  totalBySeries.sort((a, b) => b.total - a.total)

  const topSeries = totalBySeries.slice(0, MAX_MAIN_SERIES_TO_SHOW)

  // set visible false to all series not in the top
  const visibleSeries = options.series.map((singleSeries) => {
    if (!topSeries.find((s) => s.name === singleSeries.name) && singleSeries.linkedTo === undefined) {
      return { ...singleSeries, visible: false }
    }
    return { ...singleSeries, visible: true }
  })

  return visibleSeries
}

export const handleLegend = (options: ExtendedHighchartsOptions, config: GraphConfig): LegendOptions => {
  const legend: LegendOptions = { ...options.legend }

  let total = 0

  if (options.plotOptions?.column?.stacking === 'percent') {
    options.series.forEach((serie) => {
      const yAxisIndex = Number(serie.yAxis) || 0
      const { unit } = config.yAxes[yAxisIndex]

      if (unit !== 'percent') {
        const overallSerie = config.overallData!.find((s) => s.name === serie.id)

        if (overallSerie) {
          total += overallSerie.data
        }
      }
    })
  }

  legend.labelFormatter = function (this) {
    const name = normalizedName(this.name, (this as Series).chart.series)
    const series = (this as Series).userOptions
    const overallSerie = config.overallData?.find((s) => s.name === series.id)

    let overAllData = ''

    if (overallSerie) {
      const yAxis = config.yAxes.find(
        ({ name }) => name === config.series.find(({ name }) => name === overallSerie.name)?.yAxis
      )

      let value = formatValue(
        {
          precision: yAxis?.precision === undefined || yAxis.precision < 0 ? 2 : yAxis.precision,
          unit: yAxis?.unit || 'percent',
        },
        overallSerie.data
      )

      let subValue = ''

      if (total != 0 && total != overallSerie.data) {
        subValue = `(${value})`
        value = `${((overallSerie.data / total) * 100).toFixed(2)}%`
      }

      overAllData = `<span style="display: flex; flex-direction: row; align-items: baseline;">
          <span style="font-size: 2.5rem; font-weight: 300">${value}</span>
          <span style="margin-left: .5rem">${subValue}</span>
        </span>
      `
    }

    const labelName = `<span style="font-size: 1.1rem; color: ${this.color}; white-space: break-spaces;">${name}</span>`

    return `
      <span style="position: relative; z-index: 0; display: flex; flex-direction: column; min-width: 12rem;">
        ${labelName}
        ${overAllData}
      </span>
      `
  }

  legend.align = 'left'
  legend.maxHeight = 150
  legend.useHTML = true

  return legend
}

export const formatGraphTitle = (title: string): ReactNode => {
  if (title && title.includes('{') && title.includes('}')) {
    const finalStr = []
    let i = 0

    while (i < title.length) {
      const closeBracketIdx = title.indexOf('}', i)
      if (title[i] === '{' && closeBracketIdx > -1) {
        finalStr.push(<span style={{ color: analyticsColors[0] }}>{title.substring(i + 1, closeBracketIdx)}</span>)
        i = closeBracketIdx + 1
      } else {
        finalStr.push(title[i])
        i++
      }
    }

    return finalStr
  }

  return title
}

export const formatConfig = (config?: IChartConfigApiModel[] | null): IBuilderConfig[] =>
  config?.map(
    ({
      applications,
      column,
      feature_name,
      force_kpi_field,
      graph_config_id,
      how,
      line,
      mode,
      nb_last_days_to_ignore,
      page,
      position,
      related_glossary_items,
      required_breakdown,
      section,
      series,
      title,
      want_negative_evolution,
      what,
      y_axes,
    }) => ({
      applications: applications.map(({ app_key }) => app_key) || [],
      column,
      featureName: feature_name,
      forceKPIField: force_kpi_field,
      id: graph_config_id,
      line,
      mode,
      nbLastDaysToIgnore: nb_last_days_to_ignore,
      page,
      position,
      requiredBreakdown: required_breakdown,
      section,
      series: series.map((el) => ({
        defaultBreakdown: el.default_breakdown,
        graphType: el.graph_type,
        minNbu: el.min_nbu,
        normalizedBy: el.normalized_by,
        values: el.values,
        withInf: el.with_inf,
        withSup: el.with_sup,
        withTrend: el.with_trend,
        yAxisName: el.y_axes_name,
      })),
      subsection: '',
      title,
      tooltip: {
        how,
        relatedGlossaryItems: related_glossary_items,
        what,
      },
      wantNegativeEvolution: want_negative_evolution,
      yAxes: y_axes || [],
    })
  ) || []
